import React, { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  TextField,
  DialogActions,
  MenuItem,
} from '@material-ui/core';
import Loading from '~/components/Loaders/Line';
import { useActions } from '~/hooks';
import * as lib from '~/dependencies';
import { log, integrationConfig } from '~/helpers';
import api from '~/services';
import messagesToast from '~/validations/messagesNotifications';
import { SubmitButton } from '../styles';

const { AUTO_TASK } = integrationConfig;

const endpointName = {
  18: 'zendesk/tickets',
  56: 'auto_task/tickets',
};

/**
 * @param {callback} onCreate
 * @param {string} externalId
 * @param {string} onCrcallIdeate
 * @returns Returns a void callback to indicate that, the parent component should get the ticket list updated
 */
export default function New({ onCreate, externalId, callId, integrationId }) {
  // STATES
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketOptions, setTicketOptions] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [status, setStatus] = useState(null);
  const [priority, setPriority] = useState(null);
  const [queue, setQueue] = useState(null);

  const { createTicket } = useActions();

  const handleCreateTicket = useCallback(
    async (inputTitle, inputDescription) => {
      if (!inputTitle || !inputDescription) {
        lib.ToastMessage('warn', 'Ticket title and description are required.');
        return;
      }

      if (externalId && callId) {
        try {
          setIsSubmitted(true);

          const response = await api.integrations.post.createTicket(
            endpointName[integrationId],
            externalId,
            callId,
            inputTitle,
            inputDescription,
            status,
            priority,
            queue
          );

          // FIXME: API is not returninig `ticket_id` anymore, should we remote this validation?!
          if (response.data?.ticket_id) {
            createTicket([
              {
                callId,
                integrationId,
                externalId,
                ...response.data,
              },
            ]);
          }

          onCreate();
          setTitle('');
          setDescription('');
          setIsSubmitted(false);
          lib.ToastMessage(
            'success',
            messagesToast().INTEGRATIONS.tickets.onCreatedSuccess
          );
        } catch (err) {
          lib.ToastMessage('warn', err?.message);
          log.error('Failure to create tickets. Reason: ', err);
        }
      }
    },
    [
      externalId,
      callId,
      status,
      priority,
      queue,
      createTicket,
      integrationId,
      onCreate,
    ]
  );

  useEffect(() => {
    const source = lib.axios.CancelToken.source();
    if (integrationId === AUTO_TASK) {
      // eslint-disable-next-line
      (async function() {
        try {
          setIsFetching(true);
          const responseTicketOptions = (
            await api.integrations.get.getTicketFields({
              cancelToken: source.token,
            })
          ).data;

          setTicketOptions(responseTicketOptions);
          setIsFetching(false);
        } catch (err) {
          log.error('Failure to retrive the AutoTask tickets. Reason: ', err);
        }
      })();
    }

    return () => source.cancel('Axios request canceled.');
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <FormControl fullWidth margin="dense">
        <TextField
          required
          id="ticket-title"
          label="Title"
          variant="outlined"
          color="secondary"
          disabled={isSubmitted}
          onChange={e => setTitle(e.target.value)}
        />

        {integrationId === AUTO_TASK && (
          <>
            <br />
            <TextField
              select
              labelId="ticket-status"
              id="ticket-status-select"
              variant="outlined"
              color="secondary"
              label={isFetching ? <Loading /> : 'Status'}
              onChange={e => setStatus(e.target.value)}>
              {ticketOptions?.status &&
                lib._.map(ticketOptions?.status, s => (
                  <MenuItem value={s.value}>{s.label}</MenuItem>
                ))}
            </TextField>

            <br />
            <TextField
              select
              labelId="ticket-priority"
              id="ticket-priority-select"
              variant="outlined"
              color="secondary"
              label={isFetching ? <Loading /> : 'Priority'}
              onChange={e => setPriority(e.target.value)}>
              {ticketOptions?.priorities &&
                lib._.map(ticketOptions?.priorities, s => (
                  <MenuItem value={s.value}>{s.label}</MenuItem>
                ))}
            </TextField>

            <br />
            <TextField
              select
              labelId="ticket-queue"
              id="ticket-queue-select"
              variant="outlined"
              color="secondary"
              label={isFetching ? <Loading /> : 'Queue'}
              onChange={e => setQueue(e.target.value)}>
              {ticketOptions?.queues &&
                lib._.map(ticketOptions?.queues, s => (
                  <MenuItem value={s.value}>{s.label}</MenuItem>
                ))}
            </TextField>
          </>
        )}

        <TextField
          required
          id="ticket-description"
          label="Description"
          variant="outlined"
          color="secondary"
          multiline
          rows={4}
          disabled={isSubmitted}
          style={{ marginTop: 15 }}
          onChange={e => setDescription(e.target.value)}
        />
      </FormControl>

      {/* TODO: Include a loading when button is pressed or while isSubmitted is true */}
      <DialogActions>
        <SubmitButton
          onClick={() => handleCreateTicket(title, description)}
          disabled={isSubmitted}>
          Save
        </SubmitButton>
      </DialogActions>
    </>
  );
}

New.propTypes = {
  onCreate: lib.PropTypes.func.isRequired,
  externalId: lib.PropTypes.string.isRequired,
  integrationId: lib.PropTypes.number.isRequired,
  callId: lib.PropTypes.string.isRequired,
};
